import { useMemo } from "react";
import { useConfigStore } from "../../store/configStore";
import { useEditorStore } from "../../store/editorStore";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ImageIcon } from "lucide-react";
import { buildStaticLayerRows } from "../../lib/staticLayerStack";
import { ProSkuCount } from "./ProSkuCount";

interface StaticLayerPreviewProps {
  /** 嵌入「关联 Spine」预览卡片下方时使用，去掉外层 Card */
  embedded?: boolean;
  /** 关联静态模版等：用其 config，而非主模版 templateConfig */
  templateConfigOverride?: {
    selection?: any[];
    skus?: Record<string, any>;
  } | null;
  /** 区块标题（关联模版 id 等） */
  sectionLabel?: string;
}

/**
 * 纯静态叠层预览：按 Selection playIndex 叠放，仅含当前 selectedSkins 中选中的 SKU 对应层。
 * 在 {@link SpinePreview} 中于「静态主模版、无 WebGL、且暂无合并静态层」时作为整卡展示。
 */
export function StaticLayerPreview({
  embedded,
  templateConfigOverride,
  sectionLabel,
}: StaticLayerPreviewProps) {
  const mainConfig = useConfigStore((s) => s.templateConfig);
  const selectedSkins = useEditorStore((s) => s.selectedSkins);
  const templateConfig = templateConfigOverride ?? mainConfig;

  const layers = useMemo(() => {
    return buildStaticLayerRows(templateConfig, selectedSkins).map(
      ({ key, url }) => ({
        key,
        url,
      }),
    );
  }, [templateConfig, selectedSkins]);

  const label =
    sectionLabel ??
    (templateConfigOverride ? "关联静态图层" : "静态图层（本模版）");

  const stack = (
    <div
      className="relative isolate z-0 w-full aspect-square rounded-md overflow-hidden bg-muted/40 border border-border"
      style={{
        backgroundImage: `
                linear-gradient(45deg, #e0e0e0 25%, transparent 25%),
                linear-gradient(-45deg, #e0e0e0 25%, transparent 25%),
                linear-gradient(45deg, transparent 75%, #e0e0e0 75%),
                linear-gradient(-45deg, transparent 75%, #e0e0e0 75%)
              `,
        backgroundSize: "20px 20px",
        backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px",
        backgroundColor: "#f5f5f5",
      }}
    >
      {/* 仅主模版整卡预览展示；embedded 关联预览不展示，避免误读关联模版数据 */}
      {!embedded && !templateConfigOverride && <ProSkuCount />}
      {layers.length === 0 ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground text-sm text-center px-4">
          <ImageIcon className="h-10 w-10 opacity-50" />
          <p>在 Selection 中为 SKU 配置图片后将在此叠放预览</p>
        </div>
      ) : (
        layers.map(({ key, url }, i) => (
          <img
            key={key}
            src={url}
            alt=""
            className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
            style={{ zIndex: i + 1 }}
            draggable={false}
          />
        ))
      )}
    </div>
  );

  if (embedded) {
    return (
      <div className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        {stack}
      </div>
    );
  }

  return (
    <div className="h-full flex">
      <Card className="flex flex-col flex-1">
        <CardHeader className="p-4 pl-6 flex-shrink-0">
          <CardTitle className="text-lg">静态预览</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0 flex-1 min-h-0">{stack}</CardContent>
      </Card>
    </div>
  );
}
